<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :filename-prefix="title" :date-range-to-force="dateRangeToForce"
      :use-stored-date-range="false" :date-picker-format="datePickerFormat"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <small class="text-muted">{{ legend }}</small>
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :table-number="data.tableNumber"
      :data-table="data.dataTable" :dimension-fields="data.dimensionColumnsTables"
      :measure-fields="data.measureColumnsTables" :table-header="data.tableHeader" :group-headers="data.groupHeaders"
      :default-sorted-column="data.defaultSortedColumnTables" :totals-data="data.totalsData"
      :data-ready="dataReadyTables" :custom-table-class="data.customTableClass" ref="tableRenderers" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import { revenueSubTypeEnum } from "@/model/businessEnum";
import {
  columnTypeEnum,
  groupKeyEnum,
  calculatedColumnsEnum,
  alternativeColumnLabelEnum,
} from "@/model/columnsEnums";
import { dateFormatEnum } from "@/filter/dateFormat";
import { Accounting } from "@/router/routeNames";
import abortMixin from "@/mixins/abortMixin";
import _ from "lodash";

export default {
  name: Accounting,
  components: {
    TableRenderer,
    commonActions,
  },
  mixins: [abortMixin],
  data() {
    return {
      /** Tableau de filtres tels qu'attendu par cubeJs.
       * Ex :
       *    [
       *      {
       *        dimension: "Site.label",
       *        operator: "equals",
       *        values: ["Site 1"]
       *      },
       *      {
       *        dimension: "BusinessDate.id",
       *        operator: "inDateRange",
       *        values: ["2020-09-23", "2020-09-26"]
       *      },
       */
      commonFilters: [],
      timeDimension: null,
      siteValues: [],
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      dataReadyTables: false,
      measureDynamicColumns: [],
      tablesData: [],
    };
  },
  computed: {
    legend() {
      return this.$t("vFullHourlyAccounting.legend");
    },
    dateRangeFilterValues() {
      return this.timeDimension ? this.timeDimension.dateRange : [];
    },
    title() {
      return this.$t("title.accounting");
    },
    dateRangeToForce() {
      // Période par défaut : le mois précédent, au complet
      const lastMonth = Tools.BusinessDateHelper.getTodaysBusinessDate().subtract(
        1,
        "months"
      );
      return [
        lastMonth.startOf("month").format(this.datePickerFormat),
        lastMonth.endOf("month").format(this.datePickerFormat),
      ];
    },
    extraRevenueColumnKeyStart() {
      return `${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT}##extra`;
    },
    dimensionColumnsTables() {
      var dimensions = TableTools.Helper.constructColumnTable(
        [calculatedColumnsEnum.DATE],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
      dimensions = dimensions.map((col) => {
        if (col.key == calculatedColumnsEnum.DATE) {
          col.tdClass = "col-total col-small";
        }
        return col;
      });
      return dimensions;
    },
    measureColumnsTables() {
      let nonDynamicMeasures = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.REVENUE_TTC_LONG,
          calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_GAMES,
          calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_SNACK,
          calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_MERCH,
          calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_OMNIPLAY,
          calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_EXTRA,
          alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN,
          calculatedColumnsEnum.TOKEN_QUANTITY_SPENT,
          alternativeColumnLabelEnum.PURGEHISTORY_TOKEN,
          alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB,
          alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH,
          alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL,
          calculatedColumnsEnum.TOKEN_DIFFERENCE,
          calculatedColumnsEnum.TTC_DIFFERENCE,
          calculatedColumnsEnum.RUNNINGTOTALS_TOKEN_DIFFERENCE,
          calculatedColumnsEnum.RUNNINGTOTALS_TTC_DIFFERENCE,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );
      nonDynamicMeasures = nonDynamicMeasures.map((col) => {
        if (
          col.key == calculatedColumnsEnum.REVENUE_TTC_LONG ||
          col.key == alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL
        ) {
          col.tdClass = "col-total col-align-right";
        } else if (
          col.key == alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN ||
          col.key == calculatedColumnsEnum.TOKEN_QUANTITY_SPENT ||
          col.key == alternativeColumnLabelEnum.PURGEHISTORY_TOKEN
        ) {
          col.tdClass = "col-total";
        }
        return col;
      });

      if (this.measureDynamicColumns.length)
        return nonDynamicMeasures
          .slice(0, 1)
          .concat(this.measureDynamicColumns)
          .concat(nonDynamicMeasures.slice(6));
      else return nonDynamicMeasures;
    },
    groupHeaders() {
      const [dynamiCol, extraDynamicCol] = this.measureDynamicColumns
        .reduce(([a, b], x) => parseFloat(x.dynamicColumnOrder) < 3000
          ? [[...a, x], b]
          : [a, [...b, x]], [[], []]);
      const hasData = this.measureDynamicColumns.length > 0;
      const dynamicNbOfMeasures = hasData ? dynamiCol.length : 4;
      const extraDynamicNbOfMeasures = hasData ? extraDynamicCol.length : 1;

      return [
        { nbOfMeasures: 1, label: "" },
        {
          nbOfMeasures: dynamicNbOfMeasures,
          label: this.$t(groupKeyEnum.GROUP_REVENUE),
        },
        {
          nbOfMeasures: extraDynamicNbOfMeasures,
          label: this.$t(groupKeyEnum.GROUP_EXCEPTIONAL),
        },
        { nbOfMeasures: 3, label: this.$t(groupKeyEnum.GROUP_PAYING_TOKENS) },
        { nbOfMeasures: 3, label: this.$t(groupKeyEnum.GROUP_KIOSK_REVENUE) },
        { nbOfMeasures: 2, label: this.$t(groupKeyEnum.GROUP_DIFFERENCE) },
        { nbOfMeasures: 2, label: this.$t(groupKeyEnum.GROUP_RUNNINGTOTALS_DIFFERENCE), },
      ].filter((group) => group.nbOfMeasures > 0);
    },
    defaultSortedColumnTables() {
      return calculatedColumnsEnum.DATE;
    },
    dimensionColumnsTables2() {
      var dimensions = TableTools.Helper.constructColumnTable(
        [calculatedColumnsEnum.TOTAL_COLUMN],
        null,
        columnTypeEnum.DIMENSION,
        false,
        null
      );
      dimensions = dimensions.map((col) => {
        if (col.key == calculatedColumnsEnum.TOTAL_COLUMN) {
          col.tdClass = "col-total";
        }
        return col;
      });
      return dimensions;
    },
    measureColumnsTables2() {
      const self = this;
      var measures = [];

      if (self.measureDynamicColumns.length) {
        measures = [...self.measureDynamicColumns].map((measure) => {
          return { ...measure, sortable: false };
        });
        _.uniq(self.measureDynamicColumns.map((measure) => measure.taxValue))
          .sort((a, b) => a - b)
          .forEach((tax) => {
            measures.push({
              key: `${calculatedColumnsEnum.TOTAL_DYNAMIC_TAXES}##${tax}`,
              taxValue: tax,
              label: self
                .$t(calculatedColumnsEnum.TOTAL_DYNAMIC_TAXES)
                .format(tax),
              sortable: false,
              tdClass: "col-total",
              type: columnTypeEnum.MEASURE,
              formatter: (value, key) => {
                return TableTools.Helper.valueFormatter(value, key);
              },
            });
          });
      } else {
        measures = TableTools.Helper.constructColumnTable(
          [
            calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_GAMES,
            calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_SNACK,
            calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_MERCH,
            calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_OMNIPLAY,
            calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_EXTRA,
          ],
          null,
          columnTypeEnum.MEASURE,
          false,
          null
        );
      }

      measures.push({
        key: calculatedColumnsEnum.TOTAL,
        label: self.$t(calculatedColumnsEnum.TOTAL),
        sortable: false,
        tdClass: "col-total-max",
        type: columnTypeEnum.MEASURE,
        formatter: (value, key) => {
          return TableTools.Helper.valueFormatter(value, key);
        },
      });
      return measures;
    },
  },
  mounted() { },
  methods: {
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      this.updateTables();
    },
    updateTables() {
      const dimensionsCommon = [
        dimensionEnum.DATE_YEAR,
        dimensionEnum.DATE_MONTH,
        dimensionEnum.DATE_DAY,
        dimensionEnum.SITE,
        dimensionEnum.SITE_NAME,
        dimensionEnum.SITE_CURRENCY,
      ];
      this.updateTableList(this.commonFilters, dimensionsCommon);
    },
    async updateTableList(filters, dimensionsCommon) {
      const { signal } = this.resetAbortController();

      try {
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString() + "-1",
            tableNumberBySite: "1",
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
            dimensionColumnsTables: self.dimensionColumnsTables,
            measureColumnsTables: self.measureColumnsTables,
            groupHeaders: self.groupHeaders,
            defaultSortedColumnTables: self.defaultSortedColumnTables,
            customTableClass: "table-width-xl export-xxl",
          };
          self.tablesData.push(table);

          var table2 = {
            tableNumber: i.toString() + "-2",
            tableNumberBySite: "2",
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
            dimensionColumnsTables: self.dimensionColumnsTables2,
            measureColumnsTables: self.measureColumnsTables2,
            customTableClass: "second-table-aligned table-width-xl export-xxl",
          };
          self.tablesData.push(table2);
        });
        // Requête liée à une pré-aggregation, à mettre à jour au besoin dans vFullHourlyAccounting.js
        var queries = [];

        queries.push({
          query: {
            measures: [
              mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALTOKENS,
              mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALAMOUNT,
            ],
            timeDimensions: [
              TableTools.Helper.stripTimeDimension(
                this.timeDimension,
                dimensionEnum.VACCOUNTINGRUNNINGTOTALS_DATE
              ),
            ],
            dimensions: dimensionsCommon.concat([
              dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE,
            ]),
            filters: [
              ...filters.filter(f => f.dimension !== dimensionEnum.TIME_HOUR),
              {
                or: [
                  {
                    dimension:
                      dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE,
                    operator: "notEquals",
                    values: ["0"], // Exclure totaux de type dépense
                  },
                  {
                    dimension: dimensionEnum.VACCOUNTINGRUNNINGTOTALS_SUBTYPE,
                    operator: "notEquals",
                    values: ["-2"], // Exclure tokens qui viennent des Events (pour la retrocompatibilité, à enlever sûrement)
                  },
                ],
              },
            ],
          },
        });

        const filtersWithGamesNotFree = [
          ...filters,
          TableTools.Helper.getFilterAccountingDataWithTokens(),
        ];

        queries.push({
          query: {
            measures: [
              mesureEnum.VFULLHOURLYACCOUNTING_TOKENS,
              mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC,
              mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT,
              mesureEnum.VFULLHOURLYACCOUNTING_EXTRA_REVENUE,
              mesureEnum.VFULLHOURLYACCOUNTING_EXTRA_REVENUE_HT,
            ],
            timeDimensions: [
              {
                ...this.timeDimension,
                dimension: dimensionEnum.VFULLHOURLYACCOUNTING_DATETIME,
              },
            ],
            dimensions: dimensionsCommon.concat([
              dimensionEnum.VFULLHOURLYACCOUNTING_ISTYPEPURCHASE,
              dimensionEnum.VFULLHOURLYACCOUNTING_SUBTYPE,
              dimensionEnum.VFULLHOURLYACCOUNTING_TAXESPERCENT,
            ]),
            filters: filtersWithGamesNotFree,
          },
        });

        let resultSets = await TableTools.Helper.doMultipleRequest(queries, {
          signal,
        });

        resultSets[0] = self.separateRunningTotalsTypes(resultSets[0]);
        resultSets[1] = self.separateByTaxesPercentAndType(resultSets[1]);

        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensionsCommon
        );
        mergedResults = TableTools.Helper.regroupDateAndTimeColumns(
          mergedResults,
          false,
          calculatedColumnsEnum.DATE
        );

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) =>
              tab.tableHeaderId === site.id && tab.tableNumberBySite === "1"
          );
          table.dataTable = mergedResults.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );
          table.dataTable = self.addCalculatedColumns(table.dataTable);

          // Récupérer la devise du site
          const siteCurrency =
            table.dataTable.length > 0
              ? table.dataTable[0][dimensionEnum.SITE_CURRENCY]
              : "EUR";

          // Mettre à jour les colonnes dynamiques avec la devise du site
          const updatedDynamicColumns = self.measureDynamicColumns.map(
            (col) => ({
              ...col,
              formatter: (value, key) => {
                return TableTools.Helper.valueFormatter(
                  value,
                  key,
                  false,
                  siteCurrency
                );
              },
            })
          );

          // Mettre à jour les colonnes de mesure avec la devise du site
          let nonDynamicMeasures = TableTools.Helper.constructColumnTable(
            [
              calculatedColumnsEnum.REVENUE_TTC_LONG,
              calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_GAMES,
              calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_SNACK,
              calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_MERCH,
              calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_OMNIPLAY,
              calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_EXTRA,
              alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN,
              calculatedColumnsEnum.TOKEN_QUANTITY_SPENT,
              alternativeColumnLabelEnum.PURGEHISTORY_TOKEN,
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB,
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH,
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL,
              calculatedColumnsEnum.TOKEN_DIFFERENCE,
              calculatedColumnsEnum.TTC_DIFFERENCE,
              calculatedColumnsEnum.RUNNINGTOTALS_TOKEN_DIFFERENCE,
              calculatedColumnsEnum.RUNNINGTOTALS_TTC_DIFFERENCE,
            ],
            null,
            columnTypeEnum.MEASURE,
            true,
            null,
            false,
            siteCurrency
          );
          nonDynamicMeasures = nonDynamicMeasures.map((col) => {
            if (
              col.key == calculatedColumnsEnum.REVENUE_TTC_LONG ||
              col.key ==
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL
            ) {
              col.tdClass = "col-total col-align-right";
            } else if (
              col.key == alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN ||
              col.key == calculatedColumnsEnum.TOKEN_QUANTITY_SPENT ||
              col.key == alternativeColumnLabelEnum.PURGEHISTORY_TOKEN
            ) {
              col.tdClass = "col-total";
            }
            return col;
          });

          if (updatedDynamicColumns.length)
            table.measureColumnsTables = nonDynamicMeasures
              .slice(0, 1)
              .concat(updatedDynamicColumns)
              .concat(nonDynamicMeasures.slice(6));
          else table.measureColumnsTables = nonDynamicMeasures;

          table.totalsData = TableTools.Helper.calculateTotalsData(
            table.dataTable,
            table.measureColumnsTables
              .filter(
                (col) =>
                  col.key !=
                  calculatedColumnsEnum.RUNNINGTOTALS_TOKEN_DIFFERENCE &&
                  col.key != calculatedColumnsEnum.RUNNINGTOTALS_TTC_DIFFERENCE
              )
              .map((col) => col.key)
          );
          // Ajouter la devise pour l'export
          if (table.dataTable.length > 0) {
            table.totalsData[dimensionEnum.SITE_CURRENCY] = siteCurrency;
          }

          // Update des colonnes pour prendre en compte les dynamiques
          table.groupHeaders = self.groupHeaders;

          // Construction du deuxième tableau de totaux
          var table2 = self.tablesData.find(
            (tab) =>
              tab.tableHeaderId === site.id && tab.tableNumberBySite === "2"
          );
          table2.dataTable = self.constructDataTable2(
            table.totalsData,
            updatedDynamicColumns,
            siteCurrency
          );

          // Mettre à jour les colonnes de mesure du tableau 2 avec la devise du site
          var measures2 = [];

          if (updatedDynamicColumns.length) {
            measures2 = [...updatedDynamicColumns].map((measure) => {
              return { ...measure, sortable: false };
            });
            _.uniq(updatedDynamicColumns.map((measure) => measure.taxValue))
              .sort((a, b) => a - b)
              .forEach((tax) => {
                measures2.push({
                  key: `${calculatedColumnsEnum.TOTAL_DYNAMIC_TAXES}##${tax}`,
                  taxValue: tax,
                  label: self
                    .$t(calculatedColumnsEnum.TOTAL_DYNAMIC_TAXES)
                    .format(tax),
                  sortable: false,
                  tdClass: "col-total",
                  type: columnTypeEnum.MEASURE,
                  formatter: (value, key) => {
                    return TableTools.Helper.valueFormatter(
                      value,
                      key,
                      false,
                      siteCurrency
                    );
                  },
                });
              });
          } else {
            measures2 = TableTools.Helper.constructColumnTable(
              [
                calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_GAMES,
                calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_SNACK,
                calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_MERCH,
                calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_OMNIPLAY,
                calculatedColumnsEnum.DISPLAY_EMPTY_COLUMN_EXTRA,
              ],
              null,
              columnTypeEnum.MEASURE,
              false,
              null,
              false,
              siteCurrency
            );
          }

          measures2.push({
            key: calculatedColumnsEnum.TOTAL,
            label: self.$t(calculatedColumnsEnum.TOTAL),
            sortable: false,
            tdClass: "col-total-max",
            type: columnTypeEnum.MEASURE,
            formatter: (value, key) => {
              return TableTools.Helper.valueFormatter(
                value,
                key,
                false,
                siteCurrency
              );
            },
          });

          table2.measureColumnsTables = measures2;

          table2.totalsData = TableTools.Helper.calculateTotalsData(
            table2.dataTable,
            table2.measureColumnsTables.map((col) => col.key)
          );
          // Ajouter la devise pour l'export
          if (table.dataTable.length > 0) {
            table2.totalsData[dimensionEnum.SITE_CURRENCY] = siteCurrency;
          }
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },

    separateRunningTotalsTypes(set) {
      set.resultSet.loadResponse.results[0].data.forEach((result) => {
        TableTools.Helper.renameKey(
          result,
          mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALTOKENS,
          result[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE]
            ? calculatedColumnsEnum.RUNNINGTOTALS_PURCHASED_TOKENS
            : calculatedColumnsEnum.RUNNINGTOTALS_SPENT_TOKENS
        );
        TableTools.Helper.renameKey(
          result,
          mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALAMOUNT,
          result[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE]
            ? calculatedColumnsEnum.RUNNINGTOTALS_PURCHASED_AMOUNT
            : calculatedColumnsEnum.RUNNINGTOTALS_SPENT_AMOUNT
        );
        delete result[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE];
      });
      return set;
    },
    separateByTaxesPercentAndType(set) {
      set.resultSet.loadResponse.results[0].data.forEach((result) => {
        const isTypePurchase =
          result[dimensionEnum.VFULLHOURLYACCOUNTING_ISTYPEPURCHASE];
        const taxValue =
          result[dimensionEnum.VFULLHOURLYACCOUNTING_TAXESPERCENT];
        const subType = result[dimensionEnum.VFULLHOURLYACCOUNTING_SUBTYPE];
        const keySuffix = `${subType}##tax${taxValue}`;
        let columnKeyWithTypeAndTax = `${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT}##${keySuffix}`;
        if (isTypePurchase) {
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC,
            `${subType === 0
              ? alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH
              : subType === 1
                ? alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB
                : alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CBDIRECT
            }##tax${taxValue}`
          );
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_TOKENS,
            `${alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN}##${keySuffix}`
          );
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_EXTRA_REVENUE,
            `${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC}##${keySuffix}##extra`
          );
          // Il faudra sommer les extra revenue CB et Cash, on prépare la colonne qui commencera par '..._extra_tax..'
          // mais on doit garder pour le moment la distinction CB / Cash dans les données pour qu'elles ne s'écrasent pas au merge
          columnKeyWithTypeAndTax = `${this.extraRevenueColumnKeyStart}##tax${taxValue}`;
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_EXTRA_REVENUE_HT,
            `${columnKeyWithTypeAndTax}##subtype${subType}`
          );

          delete result[mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT];
        } else {
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC,
            `${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC}##${keySuffix}`
          );
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_TOKENS,
            `${mesureEnum.VFULLHOURLYACCOUNTING_TOKENS}##${keySuffix}`
          );
          TableTools.Helper.renameKey(
            result,
            mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT,
            columnKeyWithTypeAndTax
          );
        }

        // Ajout de la colonne dynamique
        if (
          !this.measureDynamicColumns.some(
            (existingColumn) => existingColumn.key === columnKeyWithTypeAndTax
          )
        ) {
          const subTypeOrder = isTypePurchase
            ? 3000
            : subType === revenueSubTypeEnum.Games
              ? 1000
              : subType === 2
                ? 3200
                : 2000 + subType * 100;
          this.measureDynamicColumns.push({
            key: columnKeyWithTypeAndTax,
            dynamicColumnOrder: `${subTypeOrder}##${taxValue}`,
            taxValue: taxValue,
            label: this.$t(
              isTypePurchase
                ? calculatedColumnsEnum.EXTRA_REVENUE_DYNAMIC_TAXES
                : subType === revenueSubTypeEnum.Games
                  ? calculatedColumnsEnum.GAME_REVENUE_DYNAMIC_TAXES
                  : subType === revenueSubTypeEnum.Events
                    ? calculatedColumnsEnum.OMNIPLAY_REVENUE_DYNAMIC_TAXES
                    : calculatedColumnsEnum.SNACK_REVENUE_DYNAMIC_TAXES + subType
            ).format(taxValue),
          });
        }

        delete result[dimensionEnum.VFULLHOURLYACCOUNTING_TAXESPERCENT];
        delete result[dimensionEnum.VFULLHOURLYACCOUNTING_SUBTYPE];
        delete result[dimensionEnum.VFULLHOURLYACCOUNTING_ISTYPEPURCHASE];
      });
      // Remove obsolete dynamic columns (in case of filter change)
      this.measureDynamicColumns = this.measureDynamicColumns.filter(
        (column) =>
          // Extra column not removed because it is computed after.
          column.key.startsWith(this.extraRevenueColumnKeyStart) ||
          set.resultSet.loadResponse.results[0].data.some(
            (result) => result[column.key]
          )
      );

      // Complete and sort dynamic columns (sans formatter, il sera ajouté par site)
      this.measureDynamicColumns = this.measureDynamicColumns.map((column) => {
        column.type = columnTypeEnum.MEASURE;
        column.sortable = true;
        column.tdClass = "col-align-right";
        return column;
      });
      this.measureDynamicColumns = _.sortBy(
        this.measureDynamicColumns,
        (col) => col.dynamicColumnOrder
      );
      return set;
    },
    addCalculatedColumns(dataTable) {
      return dataTable.map((data) => {
        // Extra revenue : séparé par TVA mais à sommer CB + Cash
        this.measureDynamicColumns.forEach((col) => {
          let extracolumnprefix = `${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_HT}##extra`;
          if (col.key.startsWith(extracolumnprefix)) {
            data[col.key] =
              _.sumBy(Object.keys(data), (key) =>
                key.startsWith(col.key) ? data[key] : 0
              ) || null;
          }
        });
        // Unités payantes dépensées
        data[calculatedColumnsEnum.TOKEN_QUANTITY_SPENT] =
          _.sumBy(Object.keys(data), (key) =>
            !key.startsWith(`${mesureEnum.VFULLHOURLYACCOUNTING_TOKENS}##2`) && // Exclure les tokens expirés
              key.startsWith(mesureEnum.VFULLHOURLYACCOUNTING_TOKENS)
              ? data[key]
              : 0
          ) || null;
        // Unités payantes achetées
        data[alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN] =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN)
              ? data[key]
              : 0
          ) || null;
        // Total expired tokens
        data[alternativeColumnLabelEnum.PURGEHISTORY_TOKEN] =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(`${mesureEnum.VFULLHOURLYACCOUNTING_TOKENS}##2`) // Inclure uniquement les tokens expirés
              ? data[key]
              : 0
          ) || null;

        // CA TTC
        data[calculatedColumnsEnum.REVENUE_TTC_LONG] =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC) &&
              !key.startsWith(`${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC}##2`) && // Exclure les tokens expirés
              !key.endsWith("##extra") // Exclure les extra
              ? data[key]
              : 0
          ) || null;
        // CB / Cash Purchases (besoin de les sommer depuis qu'elles peuvent être séparées par TVA,
        // et les achats CB sont séparés entre les achats de snack/merch direct et les achats de carte)
        data[alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB] =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB
            ) ||
              key.startsWith(
                alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CBDIRECT
              )
              ? data[key]
              : 0
          ) || null;
        data[alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH] =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(
              alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH
            )
              ? data[key]
              : 0
          ) || null;

        // Total purchases
        data[alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL] =
          _.sumBy(Object.keys(data), (key) =>
            key === alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CB ||
              key === alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_CASH
              ? data[key]
              : 0
          ) || null;

        // Différence
        const tokenSpent =
          data[calculatedColumnsEnum.TOKEN_QUANTITY_SPENT] || 0;
        const tokenBought =
          data[alternativeColumnLabelEnum.PURCHASEHISTORY_TOKEN] || 0;
        const tokenExpired =
          data[alternativeColumnLabelEnum.PURGEHISTORY_TOKEN] || 0;
        data[calculatedColumnsEnum.TOKEN_DIFFERENCE] =
          tokenBought - tokenSpent - tokenExpired || null;

        // CA TTC hors events pour calculer la différence.
        const revenueExcludingEvents =
          _.sumBy(Object.keys(data), (key) =>
            key.startsWith(`${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC}##`) &&
              !key.startsWith(`${mesureEnum.VFULLHOURLYACCOUNTING_AMOUNT_TTC}##${revenueSubTypeEnum.Events}##`) &&
              !key.endsWith("##extra")
              ? data[key]
              : 0
          ) || 0;
        const cashing =
          data[alternativeColumnLabelEnum.PURCHASEHISTORY_AMOUNT_TOTAL] || 0;
        data[calculatedColumnsEnum.TTC_DIFFERENCE] =
          cashing - revenueExcludingEvents || null;

        const runningTotalsTokenSpent =
          data[calculatedColumnsEnum.RUNNINGTOTALS_SPENT_TOKENS] || 0;
        const runningTotalsTokenBought =
          data[calculatedColumnsEnum.RUNNINGTOTALS_PURCHASED_TOKENS] || 0;
        data[calculatedColumnsEnum.RUNNINGTOTALS_TOKEN_DIFFERENCE] =
          runningTotalsTokenBought - runningTotalsTokenSpent;

        const runningTotalsRevenue =
          data[calculatedColumnsEnum.RUNNINGTOTALS_SPENT_AMOUNT] || 0;
        const runningTotalsCashing =
          data[calculatedColumnsEnum.RUNNINGTOTALS_PURCHASED_AMOUNT] || 0;
        data[calculatedColumnsEnum.RUNNINGTOTALS_TTC_DIFFERENCE] =
          runningTotalsCashing - runningTotalsRevenue;

        return data;
      });
    },
    constructDataTable2(
      totalsDataTable1,
      dynamicColumns,
      siteCurrency = "EUR"
    ) {
      const self = this;
      var lineTotalsHt = {};
      var lineTotalsTaxes = {};

      // Obtenir le symbole de devise
      const { currencies } = require("@/i18n");
      const currencySymbol = currencies[siteCurrency] || "€";

      // Fausses données, pour afficher "Total HT" etc avec la devise :
      lineTotalsHt[calculatedColumnsEnum.TOTAL_COLUMN] = self
        .$t(calculatedColumnsEnum.TOTAL_DATA_HT)
        .replace("{0}", currencySymbol);
      lineTotalsTaxes[calculatedColumnsEnum.TOTAL_COLUMN] = self
        .$t(calculatedColumnsEnum.TOTAL_DATA_TAXES)
        .replace("{0}", currencySymbol);

      // Ajouter la devise
      lineTotalsHt[dimensionEnum.SITE_CURRENCY] = siteCurrency;
      lineTotalsTaxes[dimensionEnum.SITE_CURRENCY] = siteCurrency;

      // Utiliser les colonnes dynamiques mises à jour ou les colonnes de base
      const columnsToUse =
        dynamicColumns && dynamicColumns.length > 0
          ? dynamicColumns
          : self.measureDynamicColumns;

      columnsToUse.forEach((col) => {
        const dynamicTotalKey = `${calculatedColumnsEnum.TOTAL_DYNAMIC_TAXES}##${col.taxValue}`;

        lineTotalsHt[col.key] = totalsDataTable1[col.key];
        if (lineTotalsHt[dynamicTotalKey])
          lineTotalsHt[dynamicTotalKey] += lineTotalsHt[col.key] || 0;
        else lineTotalsHt[dynamicTotalKey] = lineTotalsHt[col.key] || 0;
        if (lineTotalsHt[calculatedColumnsEnum.TOTAL])
          lineTotalsHt[calculatedColumnsEnum.TOTAL] +=
            lineTotalsHt[col.key] || 0;
        else
          lineTotalsHt[calculatedColumnsEnum.TOTAL] =
            lineTotalsHt[col.key] || 0;

        lineTotalsTaxes[col.key] =
          (col.taxValue / 100) * totalsDataTable1[col.key];
        if (lineTotalsTaxes[dynamicTotalKey])
          lineTotalsTaxes[dynamicTotalKey] += lineTotalsTaxes[col.key] || 0;
        else lineTotalsTaxes[dynamicTotalKey] = lineTotalsTaxes[col.key] || 0;
        if (lineTotalsTaxes[calculatedColumnsEnum.TOTAL])
          lineTotalsTaxes[calculatedColumnsEnum.TOTAL] +=
            lineTotalsTaxes[col.key] || 0;
        else
          lineTotalsTaxes[calculatedColumnsEnum.TOTAL] =
            lineTotalsTaxes[col.key] || 0;
      });

      return [lineTotalsHt, lineTotalsTaxes];
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumnsTables,
          table.measureColumnsTables,
          table.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData,
          null,
          table.customTableClass
        );
        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        subtitles,
        filename,
        null,
        true // reduce title for export, to be printed as A3
      );
    },
  },
};
</script>
